'use client';

import clsx from 'clsx';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { Button } from '@/components/ui/button';
import { getData, type Data } from './data';
import { HomeMatchdayCard } from './HomeMatchdayCard';

export function HomeMatchdayList({
  matchdays: initialMatchdays,
}: {
  matchdays: Data;
}) {
  const [matchdays, setMatchdays] = useState(initialMatchdays);

  const [showLoadNextButton, setShowLoadNextButton] = useState(true);
  const [loadingNew, setLoadingNew] = useState(false);
  const [offsetNew, setOffsetNew] = useState(0);
  const limitNew = 10;

  async function loadNewMatchdays() {
    setLoadingNew(true);

    try {
      const newItems = await getData({
        next: true,
        limit: limitNew,
        offset: offsetNew,
      });

      const someRepeatedItems = newItems.some((newItem) =>
        matchdays.some((item) => item.id === newItem.id),
      );

      const itemsToAdd = someRepeatedItems
        ? newItems
            .filter(
              (newItem) => !matchdays.some((item) => item.id === newItem.id),
            )
            .reverse()
        : newItems.reverse();

      setMatchdays((prevItems) => [...itemsToAdd, ...prevItems]);
      setOffsetNew(limitNew);

      if (someRepeatedItems) {
        setShowLoadNextButton(false);
      }
    } catch (error) {
      console.error('Error fetching new data:', error);
    } finally {
      setLoadingNew(false);
    }
  }

  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(initialMatchdays.length);
  const [ref, inView] = useInView({ threshold: 1 });
  const limit = 5;
  const maxItems = 100;

  useEffect(() => {
    const loadMore = async () => {
      setLoading(true);

      try {
        const newItems = await getData({ limit, offset });

        setMatchdays((prevItems) => [...prevItems, ...newItems]);
        setOffset((prevOffset) => prevOffset + limit);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (inView && !loading && matchdays.length <= maxItems) {
      loadMore();
    }
  }, [inView, matchdays.length, loading, offset]);

  return (
    <div>
      {showLoadNextButton && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            className="h-9"
            disabled={loadingNew}
            onClick={loadNewMatchdays}
          >
            <Loader2
              className={clsx(
                'mr-2 h-4 w-4',
                loadingNew ? 'animate-spin' : 'hidden',
              )}
            />
            Cargar próximas jornadas
          </Button>
        </div>
      )}

      <ul className="divide-y divide-border">
        {matchdays.map((matchday) => (
          <li key={matchday.id}>
            <HomeMatchdayCard matchday={matchday} />
          </li>
        ))}
      </ul>

      <div ref={ref} className="flex justify-center h-20">
        {loading && <Loader2 className="animate-spin size-8" />}
      </div>
    </div>
  );
}
